import { edgeApi, contactsApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { handleApiError } from '@lib/handleApiError';
import type { ApiResponse, TwilioVoiceTokenResponse, IceServerArray } from '@types';
import { logInfo as logInf } from '@lib/logger/logInfo';
import { logWarning as logWarn } from '@lib/logger/logWarning';
import { logError as logErr } from '@lib/logger/logError';
import { type AxiosError } from 'axios';

export type CallSessionStatus = 'started' | 'paused' | 'finished';
export type SessionActionKind = 'transferred' | 'paused' | 'started' | 'finished';
export type SdrStatus = 'free' | 'busy' | 'on_call' | 'unknown';

export interface SessionAction {
  action: SessionActionKind | Uppercase<SessionActionKind>;
  userId?: string;
}

export interface CallSession {
  id: string;
  status: CallSessionStatus;
  scriptId?: string | null;
  scriptTitle?: string | null;
  voicemailUrl?: string | null;
  sdrId?: string | null;
  sdrName?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  sdrStatus?: SdrStatus | null;
  action?: SessionAction | null;
  speech?: string | null;
  data?: { speech?: string | null } | null;
  voiceNoteId?: string | null;
  startAt?: number;
  endsAt?: number;
  agents?: string[];
  voiceDropsIds?: string[];
  contactList?: Array<{ contactListId: string; completed: boolean }>;
}

export interface CallLogUpdateRequest {
  callId: string;
  status?: 'finished' | 'cut_off';
  disposition?: string;
  notesAgent?: string;
  notes?: string;
}

export interface Recording {
  id: string;
  url: string;
  callId?: string;
  createdAt?: string;
}

export interface PlayRecordingPayload {
  friendlyNameVM: string;
  recordingUrl: string;
}

export interface UpdateAgentsDto {
  addedIds: string[];
  removedIds: string[];
  callSessionId: string;
  userId: string;
}

const stripUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

const withSlash = (base: string): string => (base.endsWith('/') ? base : `${base}/`);

export const getCallSessionByStatusAndAgentId = async (
  status: string,
  agentId: string,
): Promise<ApiResponse<Array<Pick<CallSession, 'id' | 'status'> & Partial<CallSession>>>> => {
  try {
    const base = microServicesRoutes.contacts.get.callSession.getByStatusAndAgentIdNest;
    const url = `${withSlash(base)}${encodeURIComponent(status)}/${encodeURIComponent(agentId)}`;
    const res = await contactsApi.get<Array<Pick<CallSession, 'id' | 'status'> & Partial<CallSession>>>(url);

    await logInf('Fetched sessions by status+agent', 'Contacts/CallSession/getByStatusAndAgentId:path', { status, agentId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallSession} Error fetching by agentId', 'Contacts/CallSession/getByStatusAndAgentId:path', { status, agentId }, true);
    return handleApiError(
      '{CallSession} Error fetching call session by agentId',
      e,
      'Contacts/CallSession/getByStatusAndAgentId:path'
    );
  }
};

export const getCallSessionById = async (
  callSessionId: string,
): Promise<ApiResponse<CallSession>> => {
  try {
    const base = microServicesRoutes.contacts.get.callSession.getByIdNest;
    const url = `${withSlash(base)}${encodeURIComponent(callSessionId)}`;
    const res = await contactsApi.get<CallSession>(url);

    await logInf('Fetched call session by id', 'Contacts/CallSession/getById:path', { callSessionId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallSession} Error fetching by id', 'Contacts/CallSession/getById:path', { callSessionId }, true);
    return handleApiError(
      '{CallSession} Error fetching by id',
      e,
      'Contacts/CallSession/getById:path'
    );
  }
};

export const updateCallSession = async (
  callSessionId: string,
  body: Partial<CallSession> | Record<string, unknown>,
): Promise<ApiResponse<CallSession>> => {
  try {
    const base = microServicesRoutes.contacts.put.callSession.updateNest;
    const url = `${withSlash(base)}${encodeURIComponent(callSessionId)}`;
    const res = await contactsApi.put<CallSession>(url, body);

    await logInf('Updated call session', 'Contacts/CallSession/updateNest:path', { callSessionId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallSession} Error updating', 'Contacts/CallSession/updateNest:path', { callSessionId }, true);
    return handleApiError(
      '{CallSession} Error updating call session',
      e,
      'Contacts/CallSession/updateNest:path'
    );
  }
};

export const hangupCallSession = async (
  callSessionId: string,
): Promise<ApiResponse<CallSession>> => {
  try {
    const base = microServicesRoutes.contacts.put.callSession.hangupNest;
    const url = `${withSlash(base)}${encodeURIComponent(callSessionId)}`;
    const res = await contactsApi.put<CallSession>(url);

    await logWarn('Hung up call session', 'Contacts/CallSession/hangupNest:path', { callSessionId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallSession} Error hanging up', 'Contacts/CallSession/hangupNest:path', { callSessionId }, true);
    return handleApiError(
      '{CallSession} Error hanging up call session',
      e,
      'Contacts/CallSession/hangupNest:path'
    );
  }
};

export const updateCallLog = async (
  body: CallLogUpdateRequest & Record<string, unknown>
): Promise<ApiResponse<{ ok: boolean }>> => {
  try {
    const base = microServicesRoutes.contacts.put.callLog.updateCallLog;
    const callId = String(body.callId);
    const url = `${base.replace(/\/$/, '')}/${encodeURIComponent(callId)}`;

    const res = await contactsApi.put<{ ok: boolean }>(url, { ...body, callId });
    await logInf('Updated call log', 'Contacts/CallLog/update', { callId, status: body.status, disposition: body.disposition }, false);

    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallLog} Error updating', 'Contacts/CallLog/update', { callId: body.callId }, true);
    return handleApiError(
      '{CallLog} Error updating call log',
      e,
      'Contacts/CallLog/update'
    );
  }
};

export interface CallLog {
  id: string;
  callId: string;
  status?: string;
  agentId: string;
  disposition?: string | null;
  notesAgent?: string | null;
  createdAt?: string;
}

export const getCallLogsBySessionId = async (
  callSessionId: string
): Promise<ApiResponse<CallLog[]>> => {
  try {
    const url = `${microServicesRoutes.contacts.get.callLogs.byCallSessionId}/${callSessionId}`;

    const res = await contactsApi.get<CallLog[]>(url);

    await logInf('Fetched call log by callId', 'Contacts/CallLog/getBySessionId', { callSessionId }, false);
    return { success: true, data: res.data ?? [] };
  } catch (e) {
    const error = e as AxiosError;

    if (error.response?.status === 404 || error.response?.status === 204) {
      return { success: true, data: [] };
    }
    await logErr('{CallLog} Error getting by callId', 'Contacts/CallLog/getBySessionId', { callSessionId }, true);
    return handleApiError(
      '{CallLog} Error getting call log by callSessionId',
      e,
      'Contacts/CallLog/getBySessionId'
    );
  }
};

export const getCallLogByCallId = async (
  callId: string
): Promise<ApiResponse<CallLog | null>> => {
  try {
    const base = microServicesRoutes.contacts.get.callLogs.sdrCallLog;
    const url = `${base.replace(/\/$/, '')}/${encodeURIComponent(callId)}`;
    const res = await contactsApi.get<CallLog>(url);

    await logInf('Fetched call log by callId', 'Contacts/CallLog/getByCallId', { callId }, false);
    return { success: true, data: res.data ?? null };
  } catch (e) {
    const error = e as AxiosError;

    if (error.response?.status === 404 || error.response?.status === 204) {
      return { success: true, data: null };
    }
    await logErr('{CallLog} Error getting by callId', 'Contacts/CallLog/getByCallId', { callId }, true);
    return handleApiError(
      '{CallLog} Error getting call log by callId',
      e,
      'Contacts/CallLog/getByCallId'
    );
  }
};

export const getCallInfo = async (
  callSessionId: string
): Promise<ApiResponse<CallLog[]>> => {
  try {
    const base = microServicesRoutes.contacts.get.callLogs.callsBySession;
    const url = `${withSlash(base)}${encodeURIComponent(callSessionId)}`;
    const res = await contactsApi.get<CallLog[]>(url);

    await logInf('Fetched call logs by session', 'Contacts/CallLog/getBySession', { callSessionId }, false);
    return { success: true, data: res.data ?? [] };
  } catch (e) {
    const error = e as AxiosError;

    if (error.response?.status === 404 || error.response?.status === 204) {
      return {
        success: true,
        data: [],
      };
    }
    await logErr('{CallLog} Error getting by session', 'Contacts/CallLog/getBySession', { callSessionId }, true);
    return handleApiError(
      '{CallLog} Error getting call logs by session',
      e,
      'Contacts/CallLog/getBySession'
    );
  }
};

export const getRecordingByCallId = async (
  callId: string
): Promise<ApiResponse<Recording | null>> => {
  try {
    const url = microServicesRoutes.contacts.get.recordings.getRecordingByCallId;
    const res = await contactsApi.get<Recording | null>(url, { params: { callId } });

    await logInf('Fetched recording by callId', 'Contacts/Recording/getByCallId', { callId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{Recording} Error by callId', 'Contacts/Recording/getByCallId', { callId }, true);
    return handleApiError(
      '{Recording} Error getting recording by callId',
      e,
      'Contacts/Recording/getByCallId'
    );
  }
};

export const getRecordingsBySdrId = async (
  sdrId: string
): Promise<ApiResponse<Recording[]>> => {
  try {
    const base = microServicesRoutes.contacts.get.recordings.getRecordingsBySdrIdNest;
    const url = `${withSlash(base)}${encodeURIComponent(sdrId)}`;
    const res = await contactsApi.get<Recording[]>(url);

    await logInf('Fetched recordings by sdrId', 'Contacts/Recording/getBySdrId', { sdrId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{Recording} Error by sdrId', 'Contacts/Recording/getBySdrId', { sdrId }, true);
    return handleApiError(
      '{Recording} Error getting recordings by sdrId',
      e,
      'Contacts/Recording/getBySdrId'
    );
  }
};

export const getRecordingById = async (
  recordingId: string
): Promise<ApiResponse<Recording>> => {
  try {
    const base = microServicesRoutes.contacts.get.recordings.getRecordingById;
    const url = `${withSlash(base)}${encodeURIComponent(recordingId)}`;
    const res = await contactsApi.get<Recording>(url);

    await logInf('Fetched recording by id', 'Contacts/Recording/getById', { recordingId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{Recording} Error by id', 'Contacts/Recording/getById', { recordingId }, true);
    return handleApiError(
      '{Recording} Error getting recording by id',
      e,
      'Contacts/Recording/getById'
    );
  }
};

export const playRecording = async (
  payload: PlayRecordingPayload
): Promise<ApiResponse<{ ok: boolean }>> => {
  try {
    const base = microServicesRoutes.edge.post.recordings.playRecordingNest;
    const url = `${withSlash(base)}${encodeURIComponent(payload.friendlyNameVM)}/recording?recordingUrl=${encodeURIComponent(payload.recordingUrl)}`;
    const res = await edgeApi.post<{ ok: boolean }>(url, { recordingUrl: payload.recordingUrl });

    await logInf('Play recording requested', 'Edge/Twilio/playRecording', { friendlyNameVM: payload.friendlyNameVM }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{Recording} Error playing', 'Edge/Twilio/playRecording', { friendlyNameVM: payload.friendlyNameVM }, false);
    return handleApiError(
      '{Recording} Error playing recording',
      e,
      'Edge/Twilio/playRecording'
    );
  }
};

export const updateContactNotes = async (payload: {
  contactId: string;
  callId: string;
  notesAgent?: string | null;
  notesSDR?: string | null;
}): Promise<ApiResponse<{ ok: boolean }>> => {
  try {
    const { contactId, ...rest } = payload;
    if (contactId === '') {
      return { success: false, error: { message: 'Missing contactId' } };
    }

    const url = `/contacts/v2/notes/${encodeURIComponent(contactId)}`;
    const body = stripUndefined(rest);
    const res = await contactsApi.put<{ ok: boolean }>(url, body);

    await logInf('Updated contact notes', 'Contacts/PUT /contacts/v2/notes', { contactId }, false);

    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{Contacts} Error updating notes', 'Contacts/PUT /contacts/v2/notes', { contactId: payload.contactId }, false);
    return handleApiError(
      '{Contacts} Error updating contact notes',
      e,
      'Contacts/PUT /contacts/v2/notes'
    );
  }
};

export const getTwilioToken = async (
  identity?: string
): Promise<ApiResponse<TwilioVoiceTokenResponse>> => {
  try {
    const url = `${microServicesRoutes.edge.get.twilio.token}/${encodeURIComponent(identity ?? '')}`;
    const response = await edgeApi.get<TwilioVoiceTokenResponse>(url);

    await logInf('Fetched Twilio token', 'Edge/Twilio/getToken', { hasIdentity: Boolean(identity) }, false);
    return { success: true, data: response.data, status: response.status };
  } catch (e) {
    await logErr('{Twilio} Error getting token', 'Edge/Twilio/getToken', { identity }, false);
    return handleApiError(
      '{Twilio} Error getting Voice token',
      e,
      'Edge/Twilio/getToken'
    );
  }
};

export const getCurrentFromNumber = async (): Promise<ApiResponse<{ from: string }>> => {
  try {
    const res = await edgeApi.get<string>(microServicesRoutes.edge.get.twilio.currentNumber);
    const raw = (res.data ?? '').toString().trim();
    if (raw.length === 0) {
      return { success: false, error: { message: 'Caller ID vacío' } };
    }
    return { success: true, data: { from: raw } };
  } catch (e) {
    return handleApiError(
      '{Twilio} Error getting current Caller ID',
      e,
      'Edge/Twilio/getCurrentNumber'
    );
  }
};

type Action = SessionActionKind;

export interface CallSessionSuccessResponse extends CallSession {

}

export const setSessionAction = async (
  callSessionId: string,
  action: { action: Action; userId: string }
): Promise<ApiResponse<CallSessionSuccessResponse>> => {
  try {
    const base = microServicesRoutes.contacts.put.callSession.updateNest;
    const url = `${withSlash(base)}${encodeURIComponent(callSessionId)}`;
    const res = await contactsApi.put<CallSessionSuccessResponse>(url, { action });

    await logInf('Set session action', 'Contacts/CallSession/setAction', { callSessionId, action: action.action }, false);
    return { success: true, data: res.data };
  } catch (e) {
    await logErr('{CallSession} Error setting action', 'Contacts/CallSession/setAction', { callSessionId }, true);
    return handleApiError(
      '{CallSession} Error setting action',
      e,
      'Contacts/CallSession/setAction'
    );
  }
};

export const twilioTransferCall = async (payload: {
  callId: string;
  sdrId: string;
  organizationId: string;
  callSessionId: string;
  agentId: string;
}): Promise<ApiResponse<{ ok: boolean }>> => {
  try {
    const base = microServicesRoutes.edge.post.twilio.transferCallNest; // '/twilio/call'
    const url = `${base}/${encodeURIComponent(payload.callId)}`;
    const res = await edgeApi.post<{ ok: boolean }>(url, payload);

    await logInf('Transfer call requested', 'Edge/Twilio/transferCall', { callId: payload.callId }, false);
    return { success: true, data: res.data };
  } catch (e) {
    // Logged but not toasted: handleTransfer raises this to the agent with the
    // actual reason attached, and toasting here too would show it twice.
    await logErr('{Twilio} Error transferring call', 'Edge/Twilio/transferCall', { callId: payload.callId }, false);
    return handleApiError(
      '{Twilio} Error transferring call',
      e,
      'Edge/Twilio/transferCall'
    );
  }
};

export const fetchLiveIceServers = async (): Promise<ApiResponse<IceServerArray>> => {
  try {
    const sessionStoreRaw = localStorage.getItem('session-store');
    let token = '';

    if (sessionStoreRaw != null) {
      const parsedStore = JSON.parse(sessionStoreRaw) as {
        state?: { authToken?: string };
      };
      token = parsedStore.state?.authToken ?? '';
    }

    const response = await fetch('https://api.tendril.dev:20050/twilio/ice-servers', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }
    const rawData = (await response.json()) as IceServerArray;
    await logInf('Fetched live ICE servers successfully', 'Edge/Twilio/fetchLiveIceServers', {}, false);

    return { success: true, data: rawData };
  } catch (e: unknown) {
    await logErr('{Twilio} Error fetching live ICE servers', 'Edge/Twilio/fetchLiveIceServers', {}, false);
    return handleApiError(
      '{Twilio} Error fetching live ICE servers',
      e,
      'Edge/Twilio/fetchLiveIceServers'
    );
  }
};

/**
 * Classifies a call whose transfer never reached the user. Operations does not
 * want the agent asked about these — a lost transfer is a failed transfer — so
 * the status is written for them and the contact is left alone afterwards.
 *
 * Deliberately the same status the server records from Twilio's callback, so the
 * two paths agree instead of leaving the contact with two conflicting outcomes.
 */
export const markCallTransferError = async (callId: string): Promise<ApiResponse<boolean>> => {
  try {
    const base = microServicesRoutes.contacts.put.callLog.updateCallLog;
    const url = `${base.replace(/\/$/, '')}/${encodeURIComponent(callId)}`;

    await contactsApi.put(url, {
      callId,
      status: 'transfer_error',
    });

    await logWarn('Marked call as transfer_error', 'Contacts/CallLog/transferError', { callId }, false);

    return { success: true, data: true };
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : 'Unknown error updating call log transfer_error';

    await logErr(
      '{CallLog} Error updating to transfer_error',
      'Contacts/CallLog/transferError',
      { callId, error: message },
      false
    );

    return handleApiError(
      '{CallLog} Error updating to transfer_error',
      err,
      'Contacts/CallLog/transferError'
    );
  }
};

export const markCallCutoff = async (callId: string): Promise<ApiResponse<boolean>> => {
  try {
    const base = microServicesRoutes.contacts.put.callLog.updateCallLog;
    const url = `${base.replace(/\/$/, '')}/${encodeURIComponent(callId)}`;

    const res = await contactsApi.put<{ ok?: boolean }>(url, {
      callId,
      status: 'cut_off',
    });

    const ok = typeof res.data === 'object' && res.data !== null && 'ok' in res.data
      ? Boolean((res.data as { ok?: boolean }).ok)
      : true;

    await logWarn('Marked call as cut_off', 'Contacts/CallLog/cutoff', { callId }, false);

    return { success: true, data: ok };
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : 'Unknown error updating call log cutoff';

    await logErr(
      '{CallLog} Error updating to cut_off',
      'Contacts/CallLog/cutoff',
      { callId, error: message },
      true
    );

    return handleApiError(
      '{CallLog} Error updating to cut_off',
      err,
      'Contacts/CallLog/cutoff'
    );
  }
};
